/**
 * Список с перетаскиванием. Используется для порядка столбцов в настройках
 * доски — §9.5 разрешает менять порядок ТОЛЬКО там, на самой доске столбцы
 * не перетаскиваются.
 *
 * Клавиатура работает наравне с мышью: KeyboardSensor плюс объявления
 * для скринридера. Перетаскивание, доступное только мышью, недоступно.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";

import { cn } from "./cn";
import { useDndSensors } from "./dndSensors";
import { GripVertical } from "./motionIcons";

interface SortableListProps<T> {
  items: T[];
  itemKey: (item: T) => string;
  onReorder: (orderedKeys: string[]) => void;
  renderItem: (item: T) => ReactNode;
  disabled?: boolean;
  /** Доступное имя списка — без него объявления теряют смысл. */
  label: string;
}

export function SortableList<T>({
  items,
  itemKey,
  onReorder,
  renderItem,
  disabled = false,
  label,
}: SortableListProps<T>) {
  // Мышь, палец (долгое нажатие) и клавиатура — см. dndSensors.ts.
  const sensors = useDndSensors(sortableKeyboardCoordinates);

  const keys = items.map(itemKey);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from === -1 || to === -1) return;

    const next = [...keys];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(to, 0, moved);
    // Сервер принимает полный массив в новом порядке (§21.6 columns/order),
    // а не пару «что и куда» — так порядок не разъедется при гонке.
    onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Взят элемент ${String(active.id)}`,
          onDragOver: ({ active, over }) =>
            over ? `Элемент ${String(active.id)} над ${String(over.id)}` : "",
          onDragEnd: ({ active, over }) =>
            over
              ? `Элемент ${String(active.id)} помещён на место ${String(over.id)}`
              : `Перемещение ${String(active.id)} отменено`,
          onDragCancel: ({ active }) => `Перемещение ${String(active.id)} отменено`,
        },
      }}
    >
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-col gap-1.5" aria-label={label}>
          {items.map((item) => (
            <SortableRow key={itemKey(item)} id={itemKey(item)} disabled={disabled}>
              {renderItem(item)}
            </SortableRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  children,
  disabled,
}: {
  id: string;
  children: ReactNode;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-2",
        isDragging && "z-10 opacity-80 shadow-md",
      )}
    >
      {!disabled && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Переместить"
          className="shrink-0 cursor-grab rounded-sm text-faint hover:text-fg active:cursor-grabbing"
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}
